import React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useThemeStore, Theme } from '../../context/themeStore';
import { Tooltip } from './Tooltip';

interface ThemeToggleProps {
  showLabel?: boolean;
  size?: 'sm' | 'md';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  showLabel = false,
  size = 'sm',
}) => {
  const { theme, setTheme } = useThemeStore();

  const themes: { value: Theme; icon: React.ReactNode; label: string }[] = [
    { value: 'light', icon: <Sun size={size === 'sm' ? 14 : 16} />, label: 'Light' },
    { value: 'dark', icon: <Moon size={size === 'sm' ? 14 : 16} />, label: 'Dark' },
    { value: 'system', icon: <Monitor size={size === 'sm' ? 14 : 16} />, label: 'System' },
  ];

  return (
    <div className="inline-flex items-center p-0.5 rounded-lg border border-line-strong">
      {themes.map((t) => (
        <Tooltip key={t.value} content={t.label}>
          <button
            type="button"
            onClick={() => setTheme(t.value)}
            aria-pressed={theme === t.value}
            aria-label={t.label}
            className={`
              flex items-center gap-1.5 h-8 px-2 rounded-md text-sm font-medium
              transition-colors duration-150
              ${theme === t.value
                ? 'bg-fg text-canvas'
                : 'text-muted hover:text-fg'
              }
            `}
          >
            {t.icon}
            {showLabel && <span>{t.label}</span>}
          </button>
        </Tooltip>
      ))}
    </div>
  );
};

// Simple icon-only toggle between light and dark
export const ThemeToggleIcon: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { resolvedTheme, setTheme } = useThemeStore();

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <Tooltip content={resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode'}>
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        className={`
          inline-flex items-center justify-center h-10 w-10 rounded-lg
          border border-line-strong text-fg hover:bg-surface-2
          transition-colors duration-150
          ${className}
        `}
      >
        {resolvedTheme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
      </button>
    </Tooltip>
  );
};
